type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';
type TConfig = {
    element: HTMLElement;
    title?: string;
    content?: string;
    multiples?: { title?: string; content?: string }[];
    xAxis?: TXPosition;
    yAxis?: TYPosition;
    hasDistance?: boolean;
}

export const createTooltip = (config: TConfig): HTMLDivElement => {
    config = {
        xAxis: 'start',
        yAxis: 'end',
        hasDistance: true,
        ...config
    }
    const tooltip = document.createElement('div');
    tooltip.classList.add(
        'z-[99999]',
        'fixed',
        'py-[4px]',
        'px-[10px]',
        'rounded-[6px]',
        'bg-tooltip',
        'max-w-[300px]',
        'shadow-sm',
        'border-border-inverted'
    );

    const contentContainer = document.createElement('div');
    contentContainer.classList.add('relative');

    const createTitle = (title: string): HTMLHeadingElement => {
        const tooltipTitle = document.createElement('h3');
        tooltipTitle.classList.add('label-semibold', 'text-tooltip-foreground', 'leading-[24px]', 'break-words');
        tooltipTitle.appendChild(document.createTextNode(title));
        return tooltipTitle;
    }

    const createContent = (content: string): HTMLParagraphElement => {
        const tooltipContent = document.createElement('p');
        tooltipContent.classList.add('content-sm-regular', 'text-foreground-75', 'leading-[20px]', 'break-words');
        tooltipContent.appendChild(document.createTextNode(content));
        return tooltipContent;
    }

    if (config.title) {
        contentContainer.appendChild(createTitle(config.title));
    }

    if (config.content) {
        contentContainer.appendChild(createContent(config.content));
    }

    if (config.multiples?.length) {
        config.multiples.forEach((item, index) => {
            if (!item.title && !item.content) {
                return;
            }
            
            const itemWrapper = document.createElement('div');
            
            const hasPreviousItems = index > 0 || config.title || config.content;
            if (hasPreviousItems) {
                itemWrapper.classList.add('pt-[8px]');
            }

            if (item.title) {
                itemWrapper.appendChild(createTitle(item.title));
            }
            if (item.content) {
                itemWrapper.appendChild(createContent(item.content));
            }
            contentContainer.appendChild(itemWrapper);
        });
    }

    tooltip.appendChild(contentContainer);
    document.body.appendChild(tooltip);

    setTooltipPosition(tooltip, config);
    return tooltip;
}

function setTooltipPosition(tooltip: HTMLDivElement, config: TConfig): void {

    const { width, height } = tooltip.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    let left = calcLeftPosition(config.xAxis, config.yAxis, tooltip, config);
    let top = calcTopPosition(config.yAxis, tooltip, config);

    if (top + height > innerHeight) top = calcTopPosition('start', tooltip, config);
    if (top < 0) top = calcTopPosition('end', tooltip, config);

    if (left + width > innerWidth && config.xAxis === 'end') left = calcLeftPosition('start', config.yAxis, tooltip, config);
    if (left < 0 || left + width > innerWidth && config.xAxis === 'start') left = calcLeftPosition('end', config.yAxis, tooltip, config);

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
}

function calcTopPosition(position: TYPosition, tooltip: HTMLDivElement, config: TConfig): number {
    const { top, height } = config.element.getBoundingClientRect();
    const { height: elHeight } = tooltip.getBoundingClientRect();
    const distance = config.hasDistance ? 6 : 0;

    switch (position) {
        case 'start':
            return top - elHeight - distance;

        case 'center':
            return top + height / 2 - elHeight / 2;

        case 'end':
            return top + height + distance

        default:
            return top - elHeight - distance;
    }
}

function calcLeftPosition(positionX: TXPosition, positionY: TYPosition, tooltip: HTMLDivElement, config: TConfig): number {
    const { left, width } = config.element.getBoundingClientRect();
    const { width: elWidth } = tooltip.getBoundingClientRect();
    const distance = config.hasDistance ? 6 : 0;

    switch (positionX) {
        case 'start':
            return positionY === 'center' ? left - elWidth - distance : left;
        case 'center':
            return left + width / 2 - elWidth / 2;

        case 'end':
            return positionY === 'center' ? left + width + distance : left + width - elWidth;

        default:
            return positionY === 'center' ? left - elWidth - distance : left;
    }
}